import { useEffect, useMemo, useRef, useState } from 'react';

// Shared "Duolingo-style" UI kit — chunky 3D buttons, an animated progress
// bar, a slide-up feedback drawer, and a lightweight confetti burst. Mirrors
// the mobile app's lib/widgets/game_ui.dart so both platforms feel the same.

function shadeColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const r = Math.max(0, Math.min(255, (num >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const b = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
}

/** A solid-color button with a darker bottom "edge" that flattens when pressed. */
export function ChunkyButton({
  children,
  color = '#4F46E5',
  shadowColor,
  onClick,
  disabled = false,
  depth = 5,
  className = '',
  style = {},
}) {
  const [pressed, setPressed] = useState(false);
  const bg = disabled ? '#E2E8F0' : color;
  const shadow = disabled ? '#CBD5E1' : shadowColor || shadeColor(color, -22);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      className={`select-none rounded-2xl font-black transition-transform duration-75 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      } ${className}`}
      style={{
        background: bg,
        boxShadow: pressed ? 'none' : `0 ${depth}px 0 ${shadow}`,
        transform: pressed ? `translateY(${depth}px)` : 'translateY(0)',
        border: 'none',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Rounded animated progress bar — fills smoothly as `value` (0-1) changes. */
export function GameProgressBar({ value, color = '#059669', trackColor = '#E2E8F0', height = 14 }) {
  const pct = Math.max(0, Math.min(1, value || 0)) * 100;
  return (
    <div
      className="w-full overflow-hidden rounded-full"
      style={{ height, background: trackColor }}
    >
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}80` }}
      />
    </div>
  );
}

/** Segmented "level" progress bar — one chunky pip per question. */
export function GameSegmentBar({ total, current }) {
  return (
    <div className="flex gap-1.5 px-1">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="h-2 flex-1 rounded-full transition-colors duration-300"
          style={{
            background: i < current ? '#58CC02' : i === current ? '#1CB0F6' : '#E2E8F0',
          }}
        />
      ))}
    </div>
  );
}

/** Slide-up bottom panel shown after an answer is checked. */
export function FeedbackDrawer({ correct, title, subtitle, continueLabel, onContinue }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const bg = correct ? '#D7FFB8' : '#FFDFE0';
  const fg = correct ? '#2B6B00' : '#8B0000';
  const button = correct ? '#58CC02' : '#FF4B4B';
  const buttonShadow = correct ? '#4CAD02' : '#D93A3A';

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50"
      style={{
        background: bg,
        transform: shown ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 260ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <div className="mx-auto max-w-3xl px-5 pb-6 pt-4">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-black text-white"
            style={{ background: button }}
          >
            {correct ? '✓' : '✕'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-black leading-tight" style={{ color: fg }}>{title}</p>
            {subtitle && (
              <p className="text-sm font-semibold" style={{ color: fg, opacity: 0.85 }}>{subtitle}</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <ChunkyButton
            color={button}
            shadowColor={buttonShadow}
            onClick={onContinue}
            className="w-full py-4 text-center text-white"
          >
            {continueLabel}
          </ChunkyButton>
        </div>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ['#FFC800', '#58CC02', '#1CB0F6', '#FF4B4B', '#CE82FF', '#FF9600'];

/** Lightweight celebratory confetti burst — no external packages required. */
export function Confetti({ count = 60 }) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.3,
        duration: 1.6 + Math.random() * 0.9,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: 6 + Math.random() * 6,
      })),
    [count],
  );

  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      {particles.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: p.size,
            height: p.size * 0.6,
            background: p.color,
            borderRadius: 2,
            animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

/** Row of 3 stars, filled up to `count`. */
export function StarRow({ count, size = 20 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ fontSize: size, color: i < count ? '#FFC800' : '#CBD5E1' }}>
          ★
        </span>
      ))}
    </div>
  );
}

/** Wraps `children` and plays a brief horizontal shake on every `trigger` pulse (any truthy value change, not just false→true). */
export function ShakeWidget({ trigger, children, className = '' }) {
  const [key, setKey] = useState(0);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (trigger) setKey((k) => k + 1);
  }, [trigger]);

  return (
    <div key={key} className={trigger ? `${className} game-shake` : className}>
      <style>{`
        @keyframes game-shake-kf {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-8px); }
          40%, 60% { transform: translateX(8px); }
        }
        .game-shake { animation: game-shake-kf 400ms ease; }
      `}</style>
      {children}
    </div>
  );
}

/** Transient "🔥 Combo xN" pill — pops in with an elastic scale then fades out once; give it a fresh `key` prop on every combo bump to replay. */
export function ComboPill({ combo }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-black text-white game-combo-pop"
      style={{ boxShadow: '0 4px 12px rgba(79,70,229,0.35)' }}
    >
      <style>{`
        @keyframes game-combo-pop-kf {
          0% { transform: scale(0.3); opacity: 0; }
          40% { transform: scale(1.15); opacity: 1; }
          70% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .game-combo-pop { animation: game-combo-pop-kf 400ms cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>
      🔥 Combo x{combo}
    </span>
  );
}

/** A "+N XP" label that floats upward and fades out once on mount. */
export function FloatingXpBadge({ xp }) {
  if (xp <= 0) return null;
  return (
    <span className="pointer-events-none inline-block text-xl font-black game-float-up" style={{ color: '#D97706' }}>
      <style>{`
        @keyframes game-float-up-kf {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-40px); opacity: 0; }
        }
        .game-float-up { animation: game-float-up-kf 900ms ease-out forwards; }
      `}</style>
      +{xp} XP
    </span>
  );
}

/** Gradient entry-point banner used to launch a mini-game. Shared across games so every entry point looks and feels the same. */
export function GameBanner({ icon, title, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3.5 text-left shadow-lg shadow-indigo-300/30 transition hover:-translate-y-0.5 active:translate-y-0"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/20 text-lg">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-black text-white">{title}</span>
        <span className="block truncate text-[11px] text-indigo-100">{desc}</span>
      </span>
      <span className="shrink-0 text-white/80" aria-hidden="true">›</span>
    </button>
  );
}

/** Skeleton placeholder shown while a game hub's vocab index/topic is loading. */
export function SkeletonGrid({ rows = 5 }) {
  return (
    <div className="flex flex-col gap-3 p-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-200/70" />
      ))}
    </div>
  );
}
